import React, { createContext, useCallback, useEffect, useState, useContext, useRef } from 'react'
import { axios, context as AuthContext } from '../context/auth'
import _find from 'lodash.find'

const RehearsalContext = createContext()

const RehearsalProvider = ({ children }) => {
  const { user } = useContext(AuthContext)
  const [rehearsal, setRehearsal] = useState(null)
  const [isOwner, setIsOwner] = useState(false)
  const [isFollowing, setIsFollowing] = useState(false)
  const [rehearsalEnded, setRehearsalEnded] = useState(false)
  const [activeRehearsals, setActiveRehearsals] = useState([])
  const [loading, setLoading] = useState(false)
  const pollingIntervalRef = useRef(null)

  // Fetch active rehearsals for user's groups
  const fetchActiveRehearsals = useCallback(async () => {
    if (!user) return

    try {
      setLoading(true)
      const response = await axios.get('/api/rehearsals')
      const active = response.data.filter(r => r.active === true)
      setActiveRehearsals(active)

      // Check if user owns or is following any active rehearsal
      const userOwnedRehearsal = _find(active, r => r.owner === user._id)
      if (userOwnedRehearsal) {
        setRehearsal(userOwnedRehearsal)
        setIsOwner(true)
        setIsFollowing(false)
      }
    } catch (err) {
      console.error('Error fetching active rehearsals:', err)
    } finally {
      setLoading(false)
    }
  }, [user])

  // Polling mechanism for followers
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
  }, [])

  // Leave a rehearsal (for followers)
  const leaveRehearsal = useCallback(() => {
    stopPolling()
    setIsFollowing(false)
    setRehearsalEnded(false)
    // Keep the rehearsal object but mark as not following
    // This preserves the current view
  }, [stopPolling])

  // Dismiss ended rehearsal notification
  const dismissEndedRehearsal = useCallback(() => {
    setRehearsalEnded(false)
    setRehearsal(null)
  }, [])

  const startPolling = useCallback((rehearsalId) => {
    // Clear any existing polling
    stopPolling()

    // Poll every 3 seconds
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await axios.get(`/api/rehearsals/${rehearsalId}`)
        const updatedRehearsal = response.data

        // Update rehearsal state if content changed
        setRehearsal(prev => {
          if (!prev) {
            return updatedRehearsal
          }

          // Convert to strings for comparison to handle ObjectId differences
          const prevSong = prev.song ? String(prev.song) : null
          const updatedSong = updatedRehearsal.song ? String(updatedRehearsal.song) : null
          const prevSetlist = prev.setlist ? String(prev.setlist) : null
          const updatedSetlist = updatedRehearsal.setlist ? String(updatedRehearsal.setlist) : null

          if (prevSong !== updatedSong ||
              prevSetlist !== updatedSetlist ||
              prev.active !== updatedRehearsal.active) {
            return updatedRehearsal
          }

          return prev
        })

        // If rehearsal ended, show ended state
        if (!updatedRehearsal.active && isFollowing) {
          setRehearsalEnded(true)
          setIsFollowing(false)
          stopPolling()
        }
      } catch (err) {
        console.error('Error polling rehearsal:', err)
        // If we get a 401 or 403, stop polling as the user likely doesn't have access
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          console.error('Authentication error, stopping polling')
          leaveRehearsal()
        }
      }
    }, 3000)
  }, [stopPolling, leaveRehearsal])

  // Start a new rehearsal
  const startRehearsal = useCallback(async (groupId) => {
    if (!user) throw new Error('User not authenticated')

    try {
      setLoading(true)
      const response = await axios.post('/api/rehearsals', { groupId })
      const newRehearsal = response.data

      setRehearsal(newRehearsal)
      setIsOwner(true)
      setIsFollowing(false)

      await fetchActiveRehearsals()

      return newRehearsal
    } catch (err) {
      console.error('Error starting rehearsal:', err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [user, fetchActiveRehearsals])

  // Join a rehearsal as a follower
  const joinRehearsal = useCallback(async (rehearsalId) => {
    if (!user) throw new Error('User not authenticated')

    try {
      setLoading(true)
      const response = await axios.get(`/api/rehearsals/${rehearsalId}`)
      const joinedRehearsal = response.data

      // Check if the user is the owner
      const userIsOwner = joinedRehearsal.owner === user._id ||
                          (joinedRehearsal.owner && joinedRehearsal.owner._id === user._id)

      setRehearsal(joinedRehearsal)
      setIsOwner(userIsOwner)
      setIsFollowing(!userIsOwner)

      // Only start polling if user is a follower (not the owner)
      if (!userIsOwner) {
        startPolling(rehearsalId)
      }

      return joinedRehearsal
    } catch (err) {
      console.error('Error joining rehearsal:', err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [user, startPolling])

  // End a rehearsal (for owners)
  const endRehearsal = useCallback(async () => {
    if (!rehearsal || !user) {
      throw new Error('Cannot end rehearsal: not owner or no active rehearsal')
    }

    // Check ownership using the same logic as controls component
    const ownerId = typeof rehearsal.owner === 'object' ? rehearsal.owner._id : rehearsal.owner
    const userIsOwner = ownerId === user._id

    if (!userIsOwner) {
      throw new Error('Cannot end rehearsal: not owner')
    }

    try {
      setLoading(true)
      await axios.delete(`/api/rehearsals/${rehearsal._id}`)

      setRehearsal(null)
      setIsOwner(false)
      setIsFollowing(false)
      setRehearsalEnded(false)

      await fetchActiveRehearsals()
    } catch (err) {
      console.error('Error ending rehearsal:', err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [rehearsal, user, fetchActiveRehearsals])

  // Set context (song/setlist) for owner
  const setContext = useCallback(async (songId = null, setlistId = null) => {
    if (!rehearsal || !isOwner) return

    // Don't make API call if content hasn't changed
    if (rehearsal.song === songId && rehearsal.setlist === setlistId) {
      return
    }

    try {
      await axios.put(`/api/rehearsals/${rehearsal._id}`, {
        songid: songId,
        setlistid: setlistId
      })

      // Update local state
      setRehearsal(prev => ({
        ...prev,
        song: songId,
        setlist: setlistId
      }))
    } catch (err) {
      console.error('Error setting context:', err)
      throw err
    }
  }, [rehearsal, isOwner])

  // Fetch active rehearsals on mount
  useEffect(() => {
    if (user) {
      fetchActiveRehearsals()
    }
  }, [user, fetchActiveRehearsals])

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling()
    }
  }, [stopPolling])

  return (
    <RehearsalContext.Provider
      value={{
        rehearsal,
        isOwner,
        isFollowing,
        rehearsalEnded,
        activeRehearsals,
        loading,
        startRehearsal,
        joinRehearsal,
        leaveRehearsal,
        endRehearsal,
        setContext,
        fetchActiveRehearsals,
        dismissEndedRehearsal
      }}
    >
      {children}
    </RehearsalContext.Provider>
  )
}

const RehearsalConsumer = RehearsalContext.Consumer

export const context = RehearsalContext

export { RehearsalProvider, RehearsalConsumer }

/**
 * Start rehearsal
 *  /rehearse
 *      -- create new
 *          -- new record in rehearsal
 *      -- shows rehearsing/id in context and header
 *      -- redirects home
 *      -- setlist / song views trigger setting setlist/song in rehearsal context
 *
 *
 * Viewing a rehearsal
 *   /rehearse/{rehearsal_id}
 *
 *   listens to or pings for updates to setlist/song ids
 *   shows rehearsing in header
 *   render existing components
 */
